import React, { useState, useEffect } from "react";
import { Container, Row, Col, Button, Form } from "react-bootstrap";
import Plot from "react-plotly.js";
import { toast } from 'react-toastify';
import { courseService, questionService, analyticsService, submissionService, errorsService } from "../../services/api";
import { useCourse } from "../../contexts/CourseContext";
import '../../styles/components/features/analytics.css';

export default function ErrorAnalytics() {
    const { selectedCourse } = useCourse();
    const [course, setCourse] = useState({});
    const [questions, setQuestions] = useState([]);
    const [selectedTopic, setSelectedTopic] = useState("");
    const [selectedQuestion, setSelectedQuestion] = useState("");
    const [selectedQuestionID, setSelectedQuestionID] = useState(null);
    const [questionStatistics, setQuestionStatistics] = useState({});
    const [submissions, setSubmissions] = useState([]);
    const [errors, setErrors] = useState([]);
    const [clusteringResults, setClusteringResults] = useState(null);
    const [elbowChartData, setElbowChartData] = useState(null);
    const [chosenK, setChosenK] = useState(0);
    const [isClusteringTriggered, setIsClusteringTriggered] = useState(false);
    const [isLoadingQuestionData, setIsLoadingQuestionData] = useState(false);
    const [isClusteringLoading, setIsClusteringLoading] = useState(false);
    const MIN_DATA_POINTS_FOR_CLUSTERING = 10;
    
    // Toggle this to use mock data for testing
    const USE_MOCK_DATA = false;
    
    // Toggle this to enable debug logging
    const DEBUG_MODE = false;

    const fetchCourse = async () => {
        try {
            const courseId = selectedCourse?.CRN;
            const courseData = await courseService.getCourse(courseId);
            setCourse(courseData);
        } catch (error) {
            console.error("Error fetching course:", error);
        }
    };


    const fetchQuestions = async () => {
        try {
            const courseId = selectedCourse?.CRN;
            const questionData = await questionService.getProblemsFromCourse(courseId);
            setQuestions(questionData);
        } catch (error) {
            console.error("Error fetching questions:", error);
        }
    };

    const fetchQuestionStatistics = async (questionID) => {
        try {
            const stats = await analyticsService.getQuestionStatistics(questionID);
            setQuestionStatistics(stats);
            
            // Verification logging
            if (DEBUG_MODE) {
                console.log("=== QUESTION STATISTICS VERIFICATION ===");
                console.log("Question ID:", questionID);
                console.log("Raw API Response:", stats);
                console.log("Number of Students:", stats.numStudents);
                console.log("Total Submissions:", stats.numSubmissions);
                console.log("Average Attempts:", stats.averageAttempts);
                console.log("Average Duration:", stats.averageDuration);
                console.log("Average Hints Used:", stats.averageHintsUsed);
                console.log("Total Hints Used:", stats.totalHintsUsed);
                console.log("Pass Rate:", stats.passRate);
                console.log("========================================");
            }
        } catch (error) {
            console.error("Error fetching question statistics:", error);
        }
    };

    const fetchSubmissions = async (questionID) => {
        try {
            // Use mock data for testing if enabled
            if (USE_MOCK_DATA) {
                const mockSubmissions = [
                    { submissionID: 1, timestamp: "2026-02-01T09:00:00Z", status: "incorrect" },
                    { submissionID: 2, timestamp: "2026-02-01T09:30:00Z", status: "incorrect" },
                    { submissionID: 3, timestamp: "2026-02-01T10:00:00Z", status: "correct" },
                    { submissionID: 4, timestamp: "2026-02-01T10:30:00Z", status: "incorrect" },
                    { submissionID: 5, timestamp: "2026-02-01T11:00:00Z", status: "incorrect" },
                    { submissionID: 6, timestamp: "2026-02-01T11:30:00Z", status: "correct" },
                    { submissionID: 7, timestamp: "2026-02-01T12:00:00Z", status: "incorrect" },
                    { submissionID: 8, timestamp: "2026-02-01T12:30:00Z", status: "incorrect" },
                    { submissionID: 9, timestamp: "2026-02-01T13:00:00Z", status: "correct" },
                    { submissionID: 10, timestamp: "2026-02-01T13:30:00Z", status: "incorrect" },
                ];
                setSubmissions(mockSubmissions);
                return;
            }
            
            const submissionData = await submissionService.getSubmissionsFromQuestion(questionID);
            setSubmissions(submissionData);
            
            // Verification logging
            if (DEBUG_MODE) {
                console.log("=== SUBMISSIONS VERIFICATION ===");
                console.log("Question ID:", questionID);
                console.log("Total Submissions Fetched:", submissionData.length);
                console.log("First 5 Submissions:", submissionData.slice(0, 5));
                console.log("Submission Status Breakdown:", {
                    correct: submissionData.filter(s => s.status === "correct").length,
                    incorrect: submissionData.filter(s => s.status === "incorrect").length,
                });
                console.log("================================");
            }
        } catch (error) {
            console.error("Error fetching submissions:", error);
        }
    };

    const fetchErrors = async (questionID) => {
        try {
            if (DEBUG_MODE) {
                console.log("Fetching errors for questionID:", questionID);
            }
            
            // Use mock data for testing if enabled
            if (USE_MOCK_DATA) {
                const mockErrors = [
                    { errorID: 1, topic: "Syntax Error", message: "Missing semicolon", timestamp: "2026-02-01T10:00:00Z" },
                    { errorID: 2, topic: "Type Error", message: "Cannot read property of undefined", timestamp: "2026-02-01T10:15:00Z" },
                    { errorID: 3, topic: "Logic Error", message: "Incorrect loop condition", timestamp: "2026-02-01T10:30:00Z" },
                    { errorID: 4, topic: "Syntax Error", message: "Unexpected token", timestamp: "2026-02-01T11:00:00Z" },
                    { errorID: 5, topic: "Reference Error", message: "Variable not defined", timestamp: "2026-02-01T11:15:00Z" },
                    { errorID: 6, topic: "Type Error", message: "Cannot convert string to number", timestamp: "2026-02-01T11:30:00Z" },
                    { errorID: 7, topic: "Logic Error", message: "Off-by-one error", timestamp: "2026-02-01T12:00:00Z" },
                    { errorID: 8, topic: "Syntax Error", message: "Unmatched parenthesis", timestamp: "2026-02-01T12:15:00Z" },
                    { errorID: 9, topic: "Type Error", message: "NaN in calculation", timestamp: "2026-02-01T12:30:00Z" },
                    { errorID: 10, topic: "Logic Error", message: "Infinite loop detected", timestamp: "2026-02-01T13:00:00Z" },
                    { errorID: 11, topic: "Reference Error", message: "Function not defined", timestamp: "2026-02-01T13:15:00Z" },
                    { errorID: 12, topic: "Syntax Error", message: "Missing closing brace", timestamp: "2026-02-01T13:30:00Z" },
                ];
                if (DEBUG_MODE) {
                    console.log("Using mock error data:", mockErrors);
                    console.log("Number of errors:", mockErrors.length);
                }
                setErrors(mockErrors);
                return;
            }
            
            const errorData = await errorsService.getErrorsFromQuestion(questionID);
            if (DEBUG_MODE) {
                console.log("Received error data:", errorData);
                console.log("Number of errors:", Array.isArray(errorData) ? errorData.length : 0);
            }
            setErrors(Array.isArray(errorData) ? errorData : []);
            
            // Verification logging
            if (DEBUG_MODE) {
                console.log("=== ERRORS VERIFICATION ===");
                console.log("Question ID:", questionID);
                console.log("Total Errors Fetched:", Array.isArray(errorData) ? errorData.length : 0);
                console.log("First 5 Errors:", Array.isArray(errorData) ? errorData.slice(0, 5) : []);
                console.log("Error Topics Breakdown:", Array.isArray(errorData) ? 
                    errorData.reduce((acc, err) => {
                        acc[err.topic] = (acc[err.topic] || 0) + 1;
                        return acc;
                    }, {}) : {}
                );
                console.log("===========================");
            }
        } catch (error) {
            console.error("Error fetching errors:", error);
            console.error("Full error details:", error.response || error.message);
            setErrors([]);
        }
    };

    const performClustering = async (questionID) => {
        setIsClusteringLoading(true);
        try {
            if (DEBUG_MODE) {
                console.log("Clustering questionID:", questionID);
            }
            const clusteringData = await analyticsService.clusterErrors(questionID);
            const z = clusteringData.z;
            const errorDfNoSyntax = clusteringData.errors;
            const lowestScore = clusteringData.lowestScore;
    
            const numClusters = Object.keys(lowestScore).map((k) => parseInt(k));
            const scores = Object.values(lowestScore).map((ls) => ls[0]);
            setElbowChartData({ numClusters, scores });
    
            setClusteringResults({ z, errorDfNoSyntax, lowestScore });
            setIsClusteringTriggered(true);
            
            // Verification logging
            if (DEBUG_MODE) {
                console.log("=== CLUSTERING VERIFICATION ===");
                console.log("Question ID:", questionID);
                console.log("Number of data points:", z.length);
                console.log("Number of clusters tested:", numClusters);
                console.log("Clustering scores:", scores);
                console.log("Lowest score details:", lowestScore);
                console.log("===============================");
            }
        } catch (error) {
            console.error("Error performing clustering:", error);
            toast.error("Failed to perform clustering. Please try again.");
        } finally {
            setIsClusteringLoading(false);
        }
    };

    useEffect(() => {
        fetchCourse();
        fetchQuestions();
    }, [selectedCourse]);

    const handleTopicChange = (e) => {
        setSelectedTopic(e.target.value);
        setSelectedQuestion("");
    };

    const handleQuestionChange = async (e) => {
        const functionName = e.target.value;
        setSelectedQuestion(functionName);
    
        const questionID = questions.find((q) => q.functionName === functionName)?.questionID;
        if (questionID) {
            if (DEBUG_MODE) {
                console.log("=== QUESTION CHANGE VERIFICATION ===");
                console.log("Selected Function Name:", functionName);
                console.log("Resolved Question ID:", questionID);
                console.log("====================================");
            }
            
            // Set loading state and clear previous data
            setIsLoadingQuestionData(true);
            setSubmissions([]);
            setErrors([]);
            setIsClusteringTriggered(false);
            setClusteringResults(null);
            setElbowChartData(null);
            setChosenK(0);
            setIsClusteringLoading(false);
            setSelectedQuestionID(questionID);
            
            // Fetch all data in parallel
            await Promise.all([
                fetchQuestionStatistics(questionID),
                fetchSubmissions(questionID),
                fetchErrors(questionID)
            ]);
            
            setIsLoadingQuestionData(false);
            
            if (DEBUG_MODE) {
                console.log("=== DATA LOADING COMPLETE ===");
                console.log("Statistics loaded:", Object.keys(questionStatistics).length > 0);
                console.log("Submissions loaded:", submissions.length);
                console.log("Errors loaded:", errors.length);
                console.log("=============================");
            }
        }
    };

    const handleClusterSelection = (k) => {
        if (k >= Math.min(...elbowChartData.numClusters) && k <= Math.max(...elbowChartData.numClusters)) {
            setChosenK(k);
        } else {
            console.error("Selected number of clusters is out of bounds.");
        }
    };

    return (
        <Container className="analytics-container">
            <div className="analytics-title">
                <h1>Real-Time Error Classification Dashboard</h1>
                <h2 className="analytics-subtitle">{course.name} - {course.semester}</h2>
            </div>

            <Row className="mb-4">
                <Col md={6}>
                    <Form.Group className="analytics-form-group">
                        <Form.Label className="analytics-label">Select a Question Set</Form.Label>
                        <Form.Select className="analytics-select" value={selectedTopic} onChange={handleTopicChange}>
                            <option value="">-- Select a Topic --</option>
                            {[...new Set(questions.map((q) => q.topic))].map((topic, index) => (
                                <option key={index} value={topic}>{topic}</option>
                            ))}
                        </Form.Select>
                    </Form.Group>
                </Col>
                {selectedTopic && (
                    <Col md={6}>
                        <Form.Group className="analytics-form-group">
                            <Form.Label className="analytics-label">Select a Question</Form.Label>
                            <Form.Select className="analytics-select" value={selectedQuestion} onChange={handleQuestionChange}>
                                <option value="">-- Select a Question --</option>
                                {questions.filter((q) => q.topic === selectedTopic).map((q, index) => (
                                    <option key={index} value={q.functionName}>{q.functionName}</option>
                                ))}
                            </Form.Select>
                        </Form.Group>
                    </Col>
                )}
            </Row>

            {selectedQuestion && (
                <>
                    <div className="mb-4">
                    <h3 className="text-center">Question Statistics: {selectedQuestion || "N/A"}</h3>
                        <Row>
                            <Col md={6}>
                                <p><strong>Number of Students Attempted:</strong> {questionStatistics.numStudents || 0} 
                                    {DEBUG_MODE && (
                                        <span className="analytics-debug-field">
                                            (Field: numStudents)
                                        </span>
                                    )}
                                </p>
                                <p><strong>Total Number of Submissions:</strong> {questionStatistics.numSubmissions || 0}
                                    {DEBUG_MODE && (
                                        <span className="analytics-debug-field">
                                            (Field: numSubmissions)
                                        </span>
                                    )}
                                </p>
                                <p><strong>Average Number of Attempts:</strong> {questionStatistics.averageAttempts ? questionStatistics.averageAttempts.toFixed(2) : "0.00"}
                                    {DEBUG_MODE && (
                                        <span className="analytics-debug-field">
                                            (Field: averageAttempts)
                                        </span>
                                    )}
                                </p>
                                <p><strong>Average Time Spent on Problem:</strong> {questionStatistics.averageDuration ? questionStatistics.averageDuration.toFixed(2) : "0.00"} seconds
                                    {DEBUG_MODE && (
                                        <span className="analytics-debug-field">
                                            (Field: averageDuration)
                                        </span>
                                    )}
                                </p>
                            </Col>
                            <Col md={6}>
                                <p><strong>Average Number of Hints Used:</strong> {questionStatistics.averageHintsUsed ? questionStatistics.averageHintsUsed.toFixed(2) : "0.00"}
                                    {DEBUG_MODE && (
                                        <span className="analytics-debug-field">
                                            (Field: averageHintsUsed)
                                        </span>
                                    )}
                                </p>
                                <p><strong>Total Number of Hints Used:</strong> {questionStatistics.totalHintsUsed || 0}
                                    {DEBUG_MODE && (
                                        <span className="analytics-debug-field">
                                            (Field: totalHintsUsed)
                                        </span>
                                    )}
                                </p>
                                <p><strong>Question Pass Rate:</strong> {questionStatistics.passRate ? Math.round(questionStatistics.passRate * 100) : 0}%
                                    {DEBUG_MODE && (
                                        <span className="analytics-debug-field">
                                            (Field: passRate)
                                        </span>
                                    )}
                                </p>
                            </Col>
                        </Row>
                    </div>

                    <div className="mb-4 analytics-charts-wrapper">
                        {isLoadingQuestionData ? (
                            <div className="text-center py-5">
                                <div className="spinner-border text-primary" role="status"></div>
                            </div>
                        ) : submissions.length > 0 && errors.length > 0 ? (
                            <Row className="g-3">
                                <Col xs={12} lg={6}>
                                    <div className="analytics-chart-container">
                                        {DEBUG_MODE && (
                                            <p className="analytics-debug-count">
                                                Data points: {submissions.length} submissions
                                            </p>
                                        )}
                                        <Plot
                                            data={[
                                                {
                                                    x: submissions.map((s) => new Date(s.timestamp)),
                                                    y: Array(submissions.length).fill(1),
                                                    type: "bar",
                                                },
                                            ]}
                                            layout={{
                                                title: { text: "Submission Frequency Over Time", x: 0.5 },
                                                xaxis: { title: { text: "Submission Time" } },
                                                yaxis: { title: { text: "Number of Submissions" } },
                                                autosize: true,
                                                responsive: true,
                                                margin: { l: 50, r: 30, t: 50, b: 50 },
                                            }}
                                            useResizeHandler={true}
                                            style={{ width: "100%", height: "400px" }}
                                            config={{ responsive: true, displayModeBar: false }}
                                        />
                                    </div>
                                </Col>
                                <Col xs={12} lg={6}>
                                    <div className="analytics-chart-container">
                                        {DEBUG_MODE && (
                                            <p className="analytics-debug-count">
                                                Data points: {errors.length} errors
                                            </p>
                                        )}
                                        <Plot
                                            data={[
                                                {
                                                    x: errors.map((e) => e.topic),
                                                    y: errors.map((e) => 1),
                                                    type: "bar",
                                                    marker: { color: "orange" },
                                                },
                                            ]}
                                            layout={{
                                                title: { text: "Error Frequencies by Topic", x: 0.5 },
                                                xaxis: { title: { text: "Error Topic" } },
                                                yaxis: { title: { text: "Number of Errors" } },
                                                autosize: true,
                                                responsive: true,
                                                margin: { l: 50, r: 30, t: 50, b: 50 },
                                            }}
                                            useResizeHandler={true}
                                            style={{ width: "100%", height: "400px" }}
                                            config={{ responsive: true, displayModeBar: false }}
                                        />
                                    </div>
                                </Col>
                            </Row>
                        ) : !isLoadingQuestionData && (
                            <div className="analytics-alert analytics-alert-warning">
                                <strong>Insufficient data for charts.</strong>
                                <p>
                                    Charts require at least 1 submission and 1 error. 
                                    Currently available: {submissions.length} submission{submissions.length !== 1 ? 's' : ''}, {errors.length} error{errors.length !== 1 ? 's' : ''}
                                </p>
                            </div>
                        )}
                    </div>

                    <div className="text-center mb-4">
                        {errors.length >= MIN_DATA_POINTS_FOR_CLUSTERING ? (
                            <Button
                                onClick={() => performClustering(selectedQuestionID)}
                                className="perform-cluster-btn"
                                disabled={isClusteringLoading}
                            >
                                {isClusteringLoading ? (
                                    <>
                                        <span className="spinner-border spinner-border-sm me-2" role="status" aria-hidden="true"></span>
                                        Processing...
                                    </>
                                ) : (
                                    "Perform Clustering"
                                )}
                            </Button>
                        ) : (
                            <div className="analytics-alert analytics-alert-info">
                                <strong>Insufficient data for clustering.</strong>
                                <p>
                                    Clustering requires at least {MIN_DATA_POINTS_FOR_CLUSTERING} error data points. 
                                    Currently available: {errors.length}
                                </p>
                            </div>
                        )}
                    </div>

                    {isClusteringTriggered && clusteringResults && (
                        <>
                            <div className="mb-4">
                                <Plot
                                    data={[
                                        {
                                            x: elbowChartData.numClusters,
                                            y: elbowChartData.scores,
                                            type: "scatter",
                                            mode: "lines+markers",
                                        },
                                    ]}
                                    layout={{
                                        title: { text: "Clustering Quality (Elbow Chart)", x: 0.5 },
                                        xaxis: { title: { text: "Number of Clusters (k)" } },
                                        yaxis: { title: { text: "Clustering Quality Score" } },
                                    }}
                                />
                            </div>

                            <div className="mb-4">
                                <Form.Group className="analytics-form-group">
                                    <Form.Label className="analytics-label">Select Number of Clusters</Form.Label>
                                    <Form.Control
                                        className="analytics-input"
                                        type="number"
                                        min={0}
                                        max={Math.max(...elbowChartData.numClusters)}
                                        value={chosenK}
                                        onChange={(e) => handleClusterSelection(parseInt(e.target.value))}
                                    />
                                </Form.Group>
                            </div>

                            {chosenK > 0 && (
                                <div className="mb-4">
                                    <Row className="justify-content-center">
                                        <Col md={8} className="text-center">
                                            <Plot
                                                data={[
                                                    {
                                                        x: clusteringResults.z.map((point) => point[0]),
                                                        y: clusteringResults.z.map((point) => point[1]),
                                                        mode: "markers",
                                                        marker: {
                                                            color: clusteringResults.lowestScore[chosenK.toString()][1],
                                                            colorscale: "Viridis",
                                                        },
                                                    },
                                                ]}
                                                layout={{
                                                    title: { text: "TSNE Error Description Embeddings, Clustered", x: 0.5 },
                                                    xaxis: { title: { text: "Dimension 1" } },
                                                    yaxis: { title: { text: "Dimension 2" } },
                                                    autosize: true,
                                                    responsive: true,
                                                }}
                                            />
                                        </Col>
                                    </Row>
                                </div>
                            )}
                        </>
                    )}
                </>
            )}
        </Container>
    );
}
